import type { LandingEditorElementFrame } from './landingEditorTypes.js';
import { defaultAutoLayoutFrame, defaultAutoLayoutItem, clampAutoLayoutSize } from '@adornis/web-editor/auto-layout.js';
export {
  type WebEditorAutoLayoutFlow as LandingEditorAutoLayoutFlow,
  type WebEditorSizingMode as LandingEditorSizingMode,
  type WebEditorAutoLayoutAlignment as LandingEditorAutoLayoutAlignment,
  type WebEditorAutoLayoutFrame as LandingEditorAutoLayoutFrame,
  type WebEditorAutoLayoutItem as LandingEditorAutoLayoutItem,
  clampAutoLayoutSize,
  defaultAutoLayoutFrame,
  defaultAutoLayoutItem,
  normalizeAutoLayoutFrame,
  normalizeAutoLayoutItem,
  autoLayoutItemsFitRow
} from '@adornis/web-editor/auto-layout.js';

type Viewport = 'Desktop' | 'Mobile';

const repairGlobusViewport = (frames: Record<string, LandingEditorElementFrame>, viewport: Viewport) => {
  const autoKey = `autoLayout${viewport}` as const;
  const itemKey = `autoLayoutItem${viewport}` as const;
  const gridKey = `grid${viewport}` as const;
  const gridContainerKey = `gridContainer${viewport}` as const;
  const gridColumnsKey = `gridColumns${viewport}` as const;
  const affectedIds = ['section.globusPartner', 'globusPartner.layout', 'globusPartner.logoPanel', 'globusPartner.copy', 'globusPartner.image'];
  const alreadyValid = frames['globusPartner.layout']?.[autoKey];
  const panelGrid = frames['globusPartner.logoPanel']?.[gridKey];
  const copyGrid = frames['globusPartner.copy']?.[gridKey];
  const imageGrid = frames['globusPartner.image']?.[gridKey];
  const hasInvalidGrid = Boolean(
    frames['section.globusPartner']?.[gridContainerKey] ||
    (panelGrid && panelGrid.parentId !== 'globusPartner.layout') ||
    (copyGrid && copyGrid.parentId !== 'globusPartner.layout') ||
    (imageGrid && imageGrid.parentId !== 'globusPartner.logoPanel'),
  );
  if (alreadyValid || !hasInvalidGrid) return;

  const maskWidth = imageGrid?.maskWidth ?? panelGrid?.maskWidth ?? (viewport === 'Desktop' ? 500 : 390);
  const maskHeight = imageGrid?.maskHeight ?? panelGrid?.maskHeight ?? (viewport === 'Desktop' ? 570 : 488);
  const layout = frames['globusPartner.layout'] ?? { x: 0, y: 0 };
  const panel = frames['globusPartner.logoPanel'] ?? { x: 0, y: 0 };
  const copy = frames['globusPartner.copy'] ?? { x: 0, y: 0 };
  layout[autoKey] = {
    ...defaultAutoLayoutFrame(viewport === 'Desktop' ? 'wrap' : 'vertical'),
    gapX: viewport === 'Desktop' ? 64 : 24,
    gapY: 34,
    alignY: viewport === 'Desktop' ? 'center' : 'start',
  };
  panel[itemKey] = {
    ...defaultAutoLayoutItem(0),
    widthMode: viewport === 'Desktop' ? 'fixed' : 'fill',
    heightMode: 'fixed',
    ...(viewport === 'Desktop' ? { fixedWidth: clampAutoLayoutSize(maskWidth) } : {}),
    fixedHeight: clampAutoLayoutSize(maskHeight),
  };
  copy[itemKey] = { ...defaultAutoLayoutItem(1), widthMode: 'fill' };
  copy[autoKey] = { ...defaultAutoLayoutFrame('vertical'), gapX: 0, gapY: 22 };
  frames['globusPartner.layout'] = layout;
  frames['globusPartner.logoPanel'] = panel;
  frames['globusPartner.copy'] = copy;
  ['globusPartner.title', 'globusPartner.text', 'globusPartner.primaryAction.label'].forEach((id, order) => {
    const child = frames[id] ?? { x: 0, y: 0 };
    child[itemKey] = { ...defaultAutoLayoutItem(order), widthMode: order < 2 ? 'fill' : 'hug' };
    frames[id] = child;
  });

  affectedIds.forEach(id => {
    const frame = frames[id];
    if (!frame) return;
    delete frame[gridKey];
    delete frame[gridContainerKey];
    delete frame[gridColumnsKey];
  });
};

/** Repairs only the historic Globus state whose grid parent was bound to the section instead of the rendered layout. */
export const repairInvalidGlobusAutoLayout = (frames: Record<string, LandingEditorElementFrame>) => {
  repairGlobusViewport(frames, 'Desktop');
  repairGlobusViewport(frames, 'Mobile');
  return frames;
};
